import {
	useDeleteMyOrganizationMutation,
	useGetOrganizationGeneralQuery,
	useUpdateOrganizationMutation,
} from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { SettingsCard } from '../../components/ui/settings-card';
import { useAuth } from '../../hooks/useAuth';
import { AddressesCard } from './AddressesCard';
import { InvoiceRecipientsCard } from './InvoiceRecipientsCard';

/* ════════════════════════ GENERAL SETTINGS TAB ════════════════════════ */

export function GeneralSettingsTab() {
	const { user } = useAuth();
	const organizationId = user?.organizationId || '';

	/* ── Data fetching ── */
	const { data, loading, refetch } = useGetOrganizationGeneralQuery({
		variables: { organizationId },
		skip: !organizationId,
		fetchPolicy: 'no-cache',
	});

	const org = data?.getOrganization;

	/* ── Org info form ── */
	const [orgName, setOrgName] = useState('');
	const [website, setWebsite] = useState('');

	/* ── Populate when data loads ── */
	useEffect(() => {
		if (org) {
			setOrgName(org.name || '');
			setWebsite(org.websiteUrl || '');
		}
	}, [org]);

	/* ── Change detection ── */
	const hasOrgChanges =
		!!org && (orgName !== (org.name || '') || website !== (org.websiteUrl || ''));

	/* ── Mutations ── */
	const [updateOrg, { loading: savingOrg }] = useUpdateOrganizationMutation();
	const [deleteMyOrg, { loading: deletingOrg }] = useDeleteMyOrganizationMutation();

	const handleSaveOrg = async () => {
		if (!org?.id) return;
		try {
			await updateOrg({
				variables: {
					input: {
						id: org.id,
						name: orgName.trim(),
						websiteUrl: website.trim() || undefined,
					},
				},
			});
			toast.success('Organization updated');
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update');
		}
	};

	const handleCancelOrg = () => {
		if (org) {
			setOrgName(org.name || '');
			setWebsite(org.websiteUrl || '');
		}
	};

	/* ── Delete org ── */
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [confirmText, setConfirmText] = useState('');

	const handleDeleteOrg = async () => {
		try {
			await deleteMyOrg();
			toast.success('Organization deleted');
			window.location.replace(import.meta.env.VITE_PUBLIC_APP_URL || '/');
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete');
		}
	};

	/* ── Loading ── */
	if (loading) {
		return (
			<div className="flex flex-col gap-4">
				{Array.from({ length: 3 }).map((_, i) => (
					<div
						key={i}
						className="h-[200px] animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface"
					/>
				))}
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			{/* ── Organization Information ── */}
			<SettingsCard
				title="Organization information"
				description="Basic information about your organization."
				hasChanges={hasOrgChanges}
				saving={savingOrg}
				onSave={handleSaveOrg}
				onCancel={handleCancelOrg}
			>
				<div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
					<Field label="Organization name" required>
						<Input
							value={orgName}
							onChange={(e) => setOrgName(e.target.value)}
							placeholder="Your organization"
						/>
					</Field>

					<Field label="Website">
						<Input
							value={website}
							onChange={(e) => setWebsite(e.target.value)}
							placeholder="https://example.com"
						/>
					</Field>
				</div>
			</SettingsCard>

			{/* ── Addresses ── */}
			<AddressesCard />

			{/* ── Invoice recipients ── */}
			<InvoiceRecipientsCard />

			{/* ── Danger Zone ── */}
			<Card className="overflow-hidden border-destructive/30 bg-surface gap-0 py-0">
				<div className="border-b border-destructive/20 px-6 py-4">
					<h3 className="text-base font-medium text-destructive">Danger zone</h3>
					<p className="mt-0.5 text-sm text-text-3">
						Irreversible and destructive actions.
					</p>
				</div>
				<div className="p-6">
					<div className="mb-1 text-base font-medium text-text-1">
						Delete organization
					</div>
					<p className="mb-4 text-sm leading-relaxed text-text-3">
						Once you delete your organization, there is no going back. All data will be
						permanently removed.
					</p>
					<Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
						Delete organization
					</Button>
				</div>
			</Card>

			{/* ── Delete confirmation ── */}
			<Dialog
				open={deleteOpen}
				onOpenChange={(v) => {
					setDeleteOpen(v);
					if (!v) setConfirmText('');
				}}
			>
				<DialogContent className="sm:max-w-sm">
					<DialogHeader className="p-4">
						<DialogTitle>Delete organization</DialogTitle>
						<DialogDescription>
							This action cannot be undone. This will permanently delete{' '}
							<strong>{org?.name}</strong> and remove all associated data.
						</DialogDescription>
					</DialogHeader>
					<DialogBody>
						<Field label={`Type "${org?.name}" to confirm`} required>
							<Input
								value={confirmText}
								onChange={(e) => setConfirmText(e.target.value)}
								placeholder="Organization name"
							/>
						</Field>
					</DialogBody>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDeleteOpen(false)}>
							Cancel
						</Button>
						<Button
							variant="destructive"
							disabled={confirmText !== org?.name || deletingOrg}
							onClick={handleDeleteOrg}
						>
							{deletingOrg ? 'Deleting…' : 'I understand, delete this organization'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
